// SSO Authentication Hook
import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { setSSOInProgress } from '../utils/api';
import { API_URLS } from '../constants';

export type SSOProvider = 'google';

interface SSOState {
  loading: boolean;
  error: string | null;
}

interface SSOCallbackResult {
  success: boolean;
  error?: string;
  hasValidToken?: boolean;
  pendingAuth?: boolean;
}

interface SSOConfig {
  google: {
    enabled: boolean;
    clientId: string;
  };
}

export const useSSO = () => {
  const { checkSession } = useAuth();
  const [ssoState, setSSOState] = useState<Record<SSOProvider, SSOState>>({
    google: { loading: false, error: null },
  });

  const getSSOConfig = useCallback(async (): Promise<SSOConfig | null> => {
    try {
      const response = await fetch(API_URLS.AUTH.SSO.CONFIG);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch SSO config: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.sso;
    } catch (error) {
      console.error('[SSO Hook] Failed to fetch SSO config:', error);
      return null;
    }
  }, []);

  const authenticateWithSSO = useCallback((provider: SSOProvider, forceAccountSelection: boolean = false) => {

    setSSOState(prev => ({
      ...prev,
      [provider]: { loading: true, error: null }
    }));

    try {
      const returnUrl = window.location.pathname;
      localStorage.setItem('sso_return_url', returnUrl);
      
      let ssoUrl = API_URLS.AUTH.SSO.GOOGLE;
      if (forceAccountSelection) {
        ssoUrl += '?force=true';
      }
      
  
      window.location.href = ssoUrl;
    } catch (error) {
      console.error(`[SSO Hook] ${provider} authentication error:`, error);
      setSSOState(prev => ({
        ...prev,
        [provider]: { 
          loading: false, 
          error: error instanceof Error ? error.message : 'SSO authentication failed' 
        }
      }));
    }
  }, []);

  const handleSSOCallback = useCallback(async (): Promise<SSOCallbackResult> => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const userStr = urlParams.get('user');
    const error = urlParams.get('error');

    if (error) {
      return { success: false, error: decodeURIComponent(error) };
    }

    // ✅ PRIORITIZE URL parameters during SSO callback (prevents 401 errors)
    if (token && userStr) {
      try {
        // Set SSO in progress flag to optimize API requests
        setSSOInProgress(true);
        
        // Clean URL first
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Store token
        localStorage.setItem('jwt_token', token);
        
        // Wait longer for cookie propagation (especially important for Google SSO)
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Check session with reduced concurrent requests
        const sessionValid = await checkSession();
        
        // Clear SSO flag after successful authentication
        setSSOInProgress(false);
        
        // For SSO callback with valid token+user, return partial success even if session check fails
        // This allows polling to continue rather than failing immediately
        return { 
          success: sessionValid, 
          hasValidToken: true, // Signal that we have valid credentials
          pendingAuth: !sessionValid // Signal that auth is pending session propagation
        };
      } catch (error) {
        // Clear SSO flag on error
        setSSOInProgress(false);
        return { success: false, error: 'Failed to process authentication' };
      }
    }

    // Fallback
    const sessionValid = await checkSession();
    if (sessionValid) {
      window.history.replaceState({}, document.title, window.location.pathname);
      return { success: true };
    }

    return { success: false, error: 'Missing authentication data' };
  }, [checkSession]);

  const handleAccountLinking = useCallback(async (provider: SSOProvider, email: string) => {
    try {
      
      const linkingUrl = `/auth/link?provider=${provider}&email=${encodeURIComponent(email)}`;
      window.location.href = linkingUrl;
    } catch (error) {
      console.error('[SSO Hook] Account linking error:', error);
    }
  }, []);

  const clearSSOError = useCallback((provider: SSOProvider) => {
    
    setSSOState(prev => ({
      ...prev,
      [provider]: { ...prev[provider], error: null }
    }));
  }, []);

  const isLoading = Object.values(ssoState).some(state => state.loading);

  return {
    ssoState,
    isLoading,
    getSSOConfig,
    authenticateWithSSO,
    handleSSOCallback,
    handleAccountLinking,
    clearSSOError,
  };
};